import { useEffect, useRef, useState, type ChangeEvent, type KeyboardEventHandler } from "react"
import { useDebounceCallback } from "usehooks-ts"
import { useHotkey } from "@tanstack/react-hotkeys"
import { CaretDown, Check, MagnifyingGlass, X } from "@phosphor-icons/react"
import { motion, AnimatePresence } from "motion/react"
import { cn } from "~/utils/helpers"
import { Input } from "../reusable/Input"
import { Button, IconButton } from "../reusable/Button"
import { ShortcutTooltip } from "../reusable/ShortcutTooltip"
import { trackEvent } from "~/hooks/useTrackingService"
import { delay } from "lodash"
import { HOTKEYS } from "~/constants/hotkeys"
import { shortcutMeta } from "~/utils/shortcutCheatsheet"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../reusable/DropdownMenu"
import type { OptionType } from "~/models/enum"
import { useIsCoveredByFullPageDialog } from "~/components/reusable/dialogs/fullPageDialogLayer"

type Props = {
  initialSearchText: string
  onSearchTextChange: (searchText: string) => void
  placeholder: string
  className?: string
  onKeyDown?: KeyboardEventHandler<HTMLInputElement> | undefined
  onInputPause?: (searchText: string) => void
  debounceTime?: number
  searchBy?: string
  onSearchByChange?: (field: string) => void
  searchByOptions?: OptionType[]
}

function ExpandableSearchField({
  initialSearchText,
  onSearchTextChange,
  placeholder,
  className,
  onKeyDown,
  onInputPause,
  debounceTime = 500,
  searchBy,
  onSearchByChange,
  searchByOptions,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(initialSearchText.length > 0)
  const [searchValue, setSearchValue] = useState(initialSearchText)
  const [isFieldDropdownOpen, setIsFieldDropdownOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleChangeDebounced = useDebounceCallback((value: string) => {
    onInputPause?.(value)
    onSearchTextChange(value)
  }, debounceTime)

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchValue(value)
    handleChangeDebounced(value)
  }

  const handleClear = () => {
    setSearchValue("")
    onSearchTextChange("")
    if (!isExpanded) {
      setIsExpanded(true)
    }
    inputRef.current?.focus()
  }

  const handleExpand = () => {
    setIsExpanded(true)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (containerRef.current?.contains(e.relatedTarget)) return
    // Don't collapse while the field selector dropdown is open (focus is in its portal)
    if (isFieldDropdownOpen) return
    if (searchValue.length === 0) {
      setIsExpanded(false)
    }
  }

  const handleFieldDropdownOpenChange = (open: boolean) => {
    setIsFieldDropdownOpen(open)
    // Re-focus the input after the dropdown closes so the user can start typing immediately
    if (!open) {
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }

  useHotkey(
    HOTKEYS.focusSearchField.hotkey,
    () => {
      trackEvent("Search Field Focused", {
        Source: "Keyboard Shortcut",
        Shortcut: "Ctrl+F",
      })
      if (isExpanded) {
        inputRef.current?.focus()
      } else {
        handleExpand()
      }
    },
    { meta: shortcutMeta("Tables", "Search table") },
  )

  const isCoveredByDialog = useIsCoveredByFullPageDialog()
  // Only claims Escape while the input is focused, so an unfocused but expanded field doesn't
  // swallow the key from a FullPageDialog's close hotkey.
  useHotkey(
    HOTKEYS.blurSearchField.hotkey,
    (event) => {
      if (document.activeElement !== inputRef.current) return
      event.preventDefault()
      inputRef.current?.blur()
    },
    { enabled: isExpanded && !isCoveredByDialog, preventDefault: false },
  )

  // Focus input when expanded (after animation completes)
  useEffect(() => {
    if (isExpanded) {
      delay(() => {
        inputRef.current?.focus()
      }, 200)
    }
  }, [isExpanded])

  // Update expanded state when initialSearchText changes
  useEffect(() => {
    const shouldExpand = initialSearchText.length > 0 && !isExpanded
    if (shouldExpand) {
      setIsExpanded(true)
    }
    setSearchValue(initialSearchText)
  }, [initialSearchText, isExpanded])

  const currentFieldLabel = searchByOptions?.find((o) => o.value === searchBy)?.label
  const clearButton =
    searchValue.length > 0 ? (
      <button
        onClick={handleClear}
        className="absolute right-3 top-1/2 -translate-y-1/2 transform text-fg-muted cursor-pointer transition-colors"
        type="button"
      >
        <X size={16} />
      </button>
    ) : undefined

  return (
    <div ref={containerRef} className="relative flex items-center">
      <AnimatePresence mode="wait">
        {!isExpanded ? (
          <motion.div
            key="button"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.15 }}
          >
            <ShortcutTooltip label="Search" shortcut={HOTKEYS.focusSearchField.hotkey}>
              <IconButton
                size="sm"
                variant="neutral"
                type="button"
                onClick={() => {
                  trackEvent("Search Field Focused", {
                    Source: "Click",
                    Shortcut: "",
                  })
                  handleExpand()
                }}
              >
                <MagnifyingGlass size={16} />
              </IconButton>
            </ShortcutTooltip>
          </motion.div>
        ) : (
          <motion.div
            key="input"
            initial={{ width: 40, opacity: 0 }}
            animate={{ width: searchByOptions ? "auto" : 180, opacity: 1 }}
            exit={{ width: 40, opacity: 0 }}
            transition={{ duration: 0.15, ease: "easeInOut" }}
            className={cn(
              searchByOptions
                ? "flex h-7 items-center overflow-hidden rounded-full bg-base shadow-button-neutral [&:has(input:focus)]:shadow-borders-interactive-with-shadow"
                : "relative flex items-center",
            )}
          >
            {searchByOptions ? (
              <>
                <DropdownMenu onOpenChange={handleFieldDropdownOpenChange}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="neutral"
                      size="sm"
                      type="button"
                      className="h-full shrink-0 rounded-none"
                    >
                      {currentFieldLabel}
                      <CaretDown size={10} className="text-fg-muted" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {searchByOptions.map((opt) => (
                      <DropdownMenuItem
                        key={opt.value}
                        onClick={() => onSearchByChange?.(opt.value)}
                        className={cn(
                          "cursor-pointer",
                          searchBy === opt.value ? "bg-background-highlight" : "bg-base",
                        )}
                      >
                        {opt.label}
                        {searchBy === opt.value && (
                          <Check size={14} className="ml-auto text-fg-muted" />
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Input
                  ref={inputRef}
                  small
                  value={searchValue}
                  type="text"
                  placeholder={placeholder}
                  containerClassName="w-[150px]"
                  variant="invisible"
                  infoElement={clearButton}
                  onKeyDown={onKeyDown}
                  onBlur={handleBlur}
                  className={cn("pl-3", searchValue.length > 0 && "pr-8", className)}
                  onChange={handleChange}
                />
              </>
            ) : (
              <Input
                ref={inputRef}
                small
                value={searchValue}
                type="text"
                placeholder={placeholder}
                containerClassName="w-full"
                variant="default"
                infoElement={clearButton}
                onKeyDown={onKeyDown}
                onBlur={handleBlur}
                className={cn(
                  "shadow-none border border-strong",
                  searchValue.length > 0 && "pr-8",
                  className,
                )}
                onChange={handleChange}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ExpandableSearchField
